import { Language } from './thi-translations';

export type ContactFaq = { q: string; a: string; icon: 'key' | 'bug' | 'help' };

export type ContactLovable = {
  heroTitle: string;
  heroSubtitle: string;
  contactInfo: string;
  primaryChannel: string;
  primaryChannelDesc: string;
  hours: string;
  hoursValue: string;
  responseTime: string;
  emailLabel: string;
  whatsappLabel: string;
  whatsappValue: string;
  whatsappCta: string;
  faqTitle: string;
  faqs: ContactFaq[];
  deleteTitle: string;
  deleteIntro: string;
  deleteSendTo: string;
  deleteConfirm: string;
  keyLinksTitle: string;
  privacy: string;
  terms: string;
  registrationCert: string;
};

const DATA: Record<Language, ContactLovable> = {
  ar: {
    heroTitle: 'الدعم الفني',
    heroSubtitle: 'تواصل مع فريق الدعم الخاص بنا للحصول على المساعدة.',
    contactInfo: 'معلومات التواصل',
    primaryChannel: 'قناة التواصل الرئيسية',
    primaryChannelDesc:
      'الواتساب هو أسرع وسيلة للوصول إلى فريق الدعم الفني لدينا. اضغط على الزر أدناه لبدء المحادثة فورًا.',
    whatsappCta: 'تواصل عبر الواتساب',
    hours: 'ساعات العمل',
    hoursValue: 'الأحد–الخميس، 09:00–17:00 (توقيت القدس)',
    responseTime: 'نسعى للرد على كل استفسار خلال يومي عمل.',
    emailLabel: 'البريد الإلكتروني',
    whatsappLabel: 'دعم الواتساب',
    whatsappValue: '+972 54-458-5601',
    faqTitle: 'الأسئلة الشائعة',
    faqs: [
      { icon: 'key', q: 'كيف أستعيد الوصول إلى حسابي؟', a: "استخدم خيار 'نسيت كلمة المرور' في شاشة تسجيل الدخول. إذا تعذّر ذلك، راسلنا مع ذكر البريد المسجّل." },
      { icon: 'bug', q: 'كيف أبلّغ عن مشكلة تقنية؟', a: 'أرسل وصفًا للمشكلة وخطوات إعادة إنتاجها، ولقطات شاشة إن أمكن، إلى بريد الدعم.' },
      { icon: 'help', q: 'هل أحتاج لتسجيل دخول لزيارة هذه الصفحة؟', a: 'لا. هذه الصفحة عامة ومتاحة للجميع.' },
    ],
    deleteTitle: 'حذف الحساب',
    deleteIntro:
      'تُقدّر THI Offices حقّك في التحكم ببياناتك. لحذف حسابك وجميع البيانات المرتبطة به بصورة دائمة، يُرجى مراسلتنا من البريد المسجّل لدينا.',
    deleteSendTo: 'أرسل طلبك إلى:',
    deleteConfirm: 'سنؤكد هويتك ونُكمل الطلب ضمن المهلة القانونية المعمول بها.',
    keyLinksTitle: 'روابط مهمة',
    privacy: 'سياسة الخصوصية',
    terms: 'الشروط والأحكام',
    registrationCert: 'شهادة تسجيل البرنامج',
  },
  en: {
    heroTitle: 'Technical Support',
    heroSubtitle: 'Contact our support team for assistance.',
    contactInfo: 'Contact Information',
    primaryChannel: 'Primary Support Channel',
    primaryChannelDesc:
      'WhatsApp is the fastest way to reach our technical support team. Tap the button below to start chatting instantly.',
    whatsappCta: 'Chat on WhatsApp',
    hours: 'Working Hours',
    hoursValue: 'Sunday–Thursday, 09:00–17:00 (Jerusalem time)',
    responseTime: 'We aim to respond to every inquiry within two business days.',
    emailLabel: 'Email',
    whatsappLabel: 'WhatsApp Support',
    whatsappValue: '+972 54-458-5601',
    faqTitle: 'Frequently Asked Questions',
    faqs: [
      { icon: 'key', q: 'How do I regain access to my account?', a: "Use the 'Forgot Password' option on the login screen. If that doesn't work, email us with your registered email address." },
      { icon: 'bug', q: 'How do I report a technical issue?', a: 'Send a description of the problem and steps to reproduce it, with screenshots if possible, to support.' },
      { icon: 'help', q: 'Do I need to log in to visit this page?', a: 'No. This page is public and available to everyone.' },
    ],
    deleteTitle: 'Account Deletion',
    deleteIntro:
      'THI Offices values your right to control your data. To permanently delete your account and all associated data, please email us from your registered email address.',
    deleteSendTo: 'Send your request to:',
    deleteConfirm: 'We will verify your identity and complete the request within the applicable legal timeframe.',
    keyLinksTitle: 'Key links',
    privacy: 'Privacy policy',
    terms: 'Terms of service',
    registrationCert: 'Software registration certificate',
  },
  he: {
    heroTitle: 'תמיכה טכנית',
    heroSubtitle: 'צור קשר עם צוות התמיכה שלנו לקבלת סיוע.',
    contactInfo: 'פרטי התקשרות',
    primaryChannel: 'ערוץ התמיכה הראשי',
    primaryChannelDesc:
      'וואטסאפ הוא הדרך המהירה ביותר ליצור קשר עם צוות התמיכה הטכנית שלנו. לחץ על הכפתור למטה כדי להתחיל בצ׳אט מיידי.',
    whatsappCta: 'שוחח בוואטסאפ',
    hours: 'שעות פעילות',
    hoursValue: 'ראשון–חמישי, 09:00–17:00 (שעון ירושלים)',
    responseTime: 'אנו שואפים להשיב לכל פנייה תוך שני ימי עסקים.',
    emailLabel: 'אימייל',
    whatsappLabel: 'תמיכת וואטסאפ',
    whatsappValue: '+972 54-458-5601',
    faqTitle: 'שאלות נפוצות',
    faqs: [
      { icon: 'key', q: 'כיצד אקבל גישה מחדש לחשבוני?', a: "השתמש באפשרות 'שכחתי סיסמה' במסך הכניסה. אם זה לא עובד, שלח לנו אימייל עם כתובת המייל הרשומה." },
      { icon: 'bug', q: 'כיצד אדווח על בעיה טכנית?', a: 'שלח תיאור של הבעיה ושלבים לשחזורה, כולל צילומי מסך אם אפשר, לאימייל התמיכה.' },
      { icon: 'help', q: 'האם אני צריך להתחבר כדי לבקר בעמוד זה?', a: 'לא. עמוד זה ציבורי וזמין לכולם.' },
    ],
    deleteTitle: 'מחיקת חשבון',
    deleteIntro:
      'THI Offices מעריכה את זכותך לשלוט בנתוניך. למחיקה לצמיתות של חשבונך וכל הנתונים הקשורים אליו, אנא שלח לנו אימייל מכתובת המייל הרשומה שלך.',
    deleteSendTo: 'שלח את בקשתך אל:',
    deleteConfirm: 'אנו נאמת את זהותך ונשלים את הבקשה במסגרת הזמן החוקי החל.',
    keyLinksTitle: 'קישורים חשובים',
    privacy: 'מדיניות פרטיות',
    terms: 'תנאי שירות',
    registrationCert: 'אישור רישום תוכנה',
  },
};

export function getLovableContactContent(lang: Language): ContactLovable {
  return DATA[lang] ?? DATA.ar;
}
